import type { ProductionAsset } from '@/api/studio-production'

export type AssetMediaTab = 'all' | 'image' | 'video' | 'audio' | 'other'

const IMAGE_KINDS = new Set(['character_ref', 'location_ref', 'storyboard', 'take_image', 'prop'])

export const ASSET_KIND_LABEL_KEYS: Record<string, string> = {
  character_ref: 'assets.kind.character_ref',
  location_ref: 'assets.kind.location_ref',
  storyboard: 'assets.kind.storyboard',
  take_video: 'assets.kind.take_video',
  take_image: 'assets.kind.take_image',
  prop: 'assets.kind.prop',
  misc: 'assets.kind.misc',
}

export function assetMediaUrl(asset: ProductionAsset): string | undefined {
  return asset.media_url || asset.url
}

function urlLooksAudio(url: string): boolean {
  return /\.(mp3|wav|ogg|m4a|aac|flac|opus)(\?|#|$)/i.test(url)
}

function urlLooksVideo(url: string): boolean {
  return /\.(mp4|mov|m4v|avi|webm)(\?|#|$)/i.test(url)
}

export function assetMediaCategory(asset: ProductionAsset): Exclude<AssetMediaTab, 'all'> {
  const mime = (asset.mime_type ?? '').toLowerCase()
  const url = (assetMediaUrl(asset) ?? '').toLowerCase()
  const name = (asset.name ?? '').toLowerCase()

  if (mime.startsWith('video/') || asset.kind === 'take_video') return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  if (mime.startsWith('image/') || IMAGE_KINDS.has(asset.kind)) return 'image'

  if (urlLooksAudio(url) || name.includes('gen_audio') || name.includes('audio')) return 'audio'
  if (urlLooksVideo(url)) return 'video'
  if (/\.(png|jpe?g|gif|webp|svg|bmp)(\?|#|$)/i.test(url)) return 'image'

  return 'other'
}

export function assetMatchesMediaTab(asset: ProductionAsset, tab: AssetMediaTab): boolean {
  if (tab === 'all') return true
  return assetMediaCategory(asset) === tab
}

/** 系统内部标签，不在资产库 UI 展示 */
const HIDDEN_ASSET_TAGS = new Set(['script_cover', 'canvas_pending'])

export function isRegisteredAsset(asset: ProductionAsset): boolean {
  return asset.registered !== false
}

export function isVisibleAssetTag(tag: string): boolean {
  return !HIDDEN_ASSET_TAGS.has(tag.trim().toLowerCase())
}

export function visibleAssetTags(tags: string[] | undefined): string[] {
  return (tags ?? []).filter(isVisibleAssetTag)
}
